<template>
  <div class="contas-form">
    <Tabs value="0">
      <TabList>
        <Tab value="0">
          <i class="pi pi-info-circle"></i> {{ t('Informações Básicas') }}
        </Tab>
        <Tab value="1">
          <i class="pi pi-credit-card"></i> {{ t('Formas de Pagamento') }}
        </Tab>
        <Tab value="2">
          <i class="pi pi-users"></i> {{ t('Usuários e Permissões') }}
        </Tab>
      </TabList>
      <TabPanels>
        <!-- Tab 1: Basic Information -->
        <TabPanel value="0">
          <div class="form-container">
            <div class="form-row">
              <div class="form-field">
                <input-text
                  data-testid="contas-nome-input"
                  v-model="formData.conta_nome"
                  :label="t('Nome da conta')"
                  :placeholder="t('Digite o nome da conta')"
                  :required="true"
                  :validation="() => !errors.conta_nome"
                  elem-id="conta_nome">
                </input-text>
                <div v-if="errors.conta_nome" class="error-message">{{ errors.conta_nome }}</div>
              </div>

              <div class="form-field">
                <select-list
                  data-testid="contas-tipo-select"
                  v-model="formData.conta_tipo"
                  :label="t('Tipo de conta')"
                  :placeholder="t('Selecione')"
                  :itens-select="accountTypeOptions"
                  :ng-required="true"
                  :validation="() => !errors.conta_tipo"
                  elem-id="conta_tipo">
                </select-list>
                <div v-if="errors.conta_tipo" class="error-message">{{ errors.conta_tipo }}</div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-field">
                <franchise-select
                  v-model="formData.conta_franquia_id"
                  :label="t('Franquia')"
                  elem-id="conta_franquia_id">
                </franchise-select>
                <div v-if="errors.conta_franquia_id" class="error-message">{{ errors.conta_franquia_id }}</div>
              </div>

              <div class="form-field" v-if="formData.conta_tipo === 'online'">
                <input-text
                  data-testid="contas-provedor-input"
                  v-model="formData.conta_provedor"
                  :label="t('Provedor')"
                  :placeholder="t('Digite o nome do provedor')"
                  elem-id="conta_provedor">
                </input-text>
              </div>
            </div>

            <div class="form-row" v-if="formData.conta_tipo === 'online'">
              <div class="form-field">
                <input-text
                  data-testid="contas-id-externo-input"
                  v-model="formData.conta_id_externo"
                  :label="t('ID Externo')"
                  :placeholder="t('Digite o ID externo')"
                  elem-id="conta_id_externo">
                </input-text>
              </div>

              <div class="form-field">
                <input-toggle
                  data-testid="contas-permite-link-pagto-toggle"
                  v-model="permiteGerarLinkPagto"
                  :label="t('Permite gerar link de pagamento')"
                  elem-id="conta_permite_gerar_link_pagto">
                </input-toggle>
              </div>
            </div>

            <div class="form-row">
              <div class="form-field">
                <input-toggle
                  data-testid="contas-status-toggle"
                  v-model="contaStatus"
                  :label="t('Ativo')"
                  elem-id="conta_status">
                </input-toggle>
              </div>
            </div>
          </div>
        </TabPanel>

        <!-- Tab 2: Payment Methods -->
        <TabPanel value="1">
          <div class="form-container">
            <inline-message severity="info" :closable="false">
              <i class="pi pi-info-circle"></i>
              {{ t('Selecione as formas de pagamento aceitas por esta conta') }}
            </inline-message>

            <div v-if="loadingFormas" class="loading-container">
              <spinner style="width: 1rem; height: 1rem" />
              {{ t('Carregando formas de pagamento...') }}
            </div>

            <div v-else>
              <inline-message v-if="availableFormasPagamento.length === 0" severity="warn" :closable="false">
                {{ t('Nenhuma forma de pagamento disponível') }}
              </inline-message>

              <button-list-multiple
                v-else
                data-testid="contas-formas-pagamento-list"
                v-model="selectedFormasPagamento"
                :itens-select="formasPagamentoOptions"
                :label="t('Formas de Pagamento')"
                :search-placeholder="t('Buscar forma de pagamento...')"
                elem-id="formas_pagamento">
              </button-list-multiple>

              <div v-if="selectedFormasPagamento.length > 0" class="selected-count">
                <strong>{{ t('Selecionadas') }}: {{ selectedFormasPagamento.length }}</strong>
              </div>
            </div>
          </div>
        </TabPanel>

        <!-- Tab 3: Users & Permissions -->
        <TabPanel value="2">
          <div class="form-container">
            <inline-message severity="info" :closable="false">
              <i class="pi pi-info-circle"></i>
              {{ t('Adicione usuários e defina suas permissões para esta conta') }}
            </inline-message>

            <div v-if="loadingUsers" class="loading-container">
              <spinner style="width: 1rem; height: 1rem" />
              {{ t('Carregando usuários...') }}
            </div>

            <div v-else>
              <!-- Add User Section -->
              <div class="add-user-section">
                <div class="user-select-field">
                  <select-list
                    data-testid="contas-add-user-select"
                    v-model="selectedUserToAdd"
                    :label="t('Adicionar Usuário')"
                    :placeholder="t('Selecione um usuário')"
                    :itens-select="availableUsersToAddOptions"
                    elem-id="user_to_add">
                  </select-list>
                </div>
                <div class="user-button-field">
                  <label>&nbsp;</label>
                  <Button
                    data-testid="contas-add-user-button"
                    :label="t('Adicionar')"
                    icon="pi pi-plus"
                    @click="addUser"
                    :disabled="!selectedUserToAdd"
                    class="full-width-button" />
                </div>
              </div>

              <!-- Users Permission Grid -->
              <inline-message v-if="accountUsers.length === 0" severity="warn" :closable="false">
                {{ t('Nenhum usuário adicionado a esta conta') }}
              </inline-message>

              <div v-else class="users-grid">
                <div class="users-grid-header">
                  <div class="users-grid-cell">{{ t('Usuário') }}</div>
                  <div class="users-grid-cell center">{{ t('Lançar Pagamentos') }}</div>
                  <div class="users-grid-cell center">{{ t('Ver Relatórios') }}</div>
                  <div class="users-grid-cell center">{{ t('Gerar Link Pagamento') }}</div>
                  <div class="users-grid-cell center">{{ t('Ações') }}</div>
                </div>
                <div
                  v-for="(user, index) in accountUsers"
                  :key="user.usuario_id"
                  class="users-grid-row">
                  <div class="users-grid-cell">{{ getUserNome(user.usuario_id) }}</div>
                  <div class="users-grid-cell center">
                    <toggle
                      :data-testid="`contas-user-${user.usuario_id}-pagamentos-toggle`"
                      :model-value="toBool(user.permite_lancar_pagamentos)"
                      @update:model-value="(val: boolean) => updateUserPermission(index, 'permite_lancar_pagamentos', val)" />
                  </div>
                  <div class="users-grid-cell center">
                    <toggle
                      :data-testid="`contas-user-${user.usuario_id}-relatorios-toggle`"
                      :model-value="toBool(user.permite_ver_relatorios)"
                      @update:model-value="(val: boolean) => updateUserPermission(index, 'permite_ver_relatorios', val)" />
                  </div>
                  <div class="users-grid-cell center">
                    <toggle
                      :data-testid="`contas-user-${user.usuario_id}-link-pagto-toggle`"
                      :model-value="toBool(user.permite_gerar_link_pagto)"
                      @update:model-value="(val: boolean) => updateUserPermission(index, 'permite_gerar_link_pagto', val)" />
                  </div>
                  <div class="users-grid-cell center">
                    <Button
                      :data-testid="`contas-user-${user.usuario_id}-remove-button`"
                      icon="pi pi-trash"
                      severity="danger"
                      size="small"
                      @click="removeUser(index)"
                      text />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useContas } from '../../composables/useContas';
import { useFormasPagamento } from '../../composables/useFormasPagamento';
import { useUsers } from '../../composables/useUsers';
import { permissionsService } from '../../services/permissionsService';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';

type TinyintBool = 0 | 1;

interface ContaFormData {
  conta_nome: string;
  conta_tipo: string;
  conta_franquia_id: number | null;
  conta_provedor: string;
  conta_id_externo: string;
  conta_permite_gerar_link_pagto: TinyintBool;
  conta_status: TinyintBool;
}

interface AccountUser {
  usuario_id: string;
  status: TinyintBool;
  permite_lancar_pagamentos: TinyintBool;
  permite_ver_relatorios: TinyintBool;
  permite_gerar_link_pagto: TinyintBool;
}

interface FormaPagamento {
  forma_pagamento_id: number;
  forma_pagamento_nome: string;
}

interface AvailableUser {
  sub: string;
  name?: string;
  Username?: string;
  franquias?: Array<{ franquia_id: string | number; franquia_nome?: string }>;
}

interface SelectOption {
  ID: string | number;
  Name: string;
}

interface ContaResponseDados {
  conta_id?: number;
}

interface ContaCreateResponse {
  dados?: ContaResponseDados;
}

interface ContaRecord {
  conta_nome?: string;
  conta_tipo?: string;
  conta_franquia_id?: number | null;
  conta_provedor?: string;
  conta_id_externo?: string;
  conta_permite_gerar_link_pagto?: TinyintBool;
  conta_status?: TinyintBool;
  formas_pagamento?: FormaPagamento[];
  usuarios?: Array<Partial<AccountUser>>;
}

interface Props {
  isEditMode?: boolean;
  contaId?: number | null;
}

const props = withDefaults(defineProps<Props>(), {
  isEditMode: false,
  contaId: null
});

const { t } = useI18n();
const { fetchContaById, createConta, updateConta, updateFormasPagamento, updateUsuarios } = useContas();
const { listarFormasPagamento } = useFormasPagamento();
useUsers();

const formData = ref<ContaFormData>({
  conta_nome: '',
  conta_tipo: '',
  conta_franquia_id: null,
  conta_provedor: '',
  conta_id_externo: '',
  conta_permite_gerar_link_pagto: 0,
  conta_status: 1
});

const errors = ref<Record<string, string>>({});
const isLoading = ref<boolean>(false);

// Payment Methods State
const loadingFormas = ref<boolean>(false);
const availableFormasPagamento = ref<FormaPagamento[]>([]);
const selectedFormasPagamento = ref<number[]>([]);

// Users State
const loadingUsers = ref<boolean>(false);
const availableUsers = ref<AvailableUser[]>([]);
const accountUsers = ref<AccountUser[]>([]);
const selectedUserToAdd = ref<string>('');

// Boolean ↔ TinyintBool converters for input-toggle wrapper
const toBool = (v: TinyintBool | undefined | null): boolean => v === 1;
const toTinyint = (v: boolean): TinyintBool => (v ? 1 : 0);

const permiteGerarLinkPagto = computed<boolean>({
  get: () => toBool(formData.value.conta_permite_gerar_link_pagto),
  set: (val: boolean) => { formData.value.conta_permite_gerar_link_pagto = toTinyint(val); }
});

const contaStatus = computed<boolean>({
  get: () => toBool(formData.value.conta_status),
  set: (val: boolean) => { formData.value.conta_status = toTinyint(val); }
});

const accountTypeOptions = computed<SelectOption[]>(() => [
  { ID: 'online', Name: t('Online') },
  { ID: 'banco', Name: t('Banco') },
  { ID: 'pos', Name: t('POS') },
  { ID: 'dinheiro', Name: t('Dinheiro') },
  { ID: 'permuta', Name: t('Permuta') }
]);

const formasPagamentoOptions = computed<SelectOption[]>(() => {
  return availableFormasPagamento.value.map(forma => ({
    ID: forma.forma_pagamento_id,
    Name: forma.forma_pagamento_nome
  }));
});

const availableUsersToAdd = computed<AvailableUser[]>(() => {
  const accountUserIds = accountUsers.value.map(u => u.usuario_id);
  const selectedFranquiaId = formData.value.conta_franquia_id;

  return availableUsers.value.filter(u => {
    if (accountUserIds.includes(u.sub)) return false;
    if (!selectedFranquiaId) return true;
    return u.franquias && u.franquias.some(f => Number(f.franquia_id) === Number(selectedFranquiaId));
  });
});

const availableUsersToAddOptions = computed<SelectOption[]>(() => {
  return availableUsersToAdd.value.map(user => ({
    ID: user.sub,
    Name: user.name || user.Username || user.sub
  }));
});

const getUserNome = (userId: string): string => {
  const user = availableUsers.value.find(u => u.sub === userId);
  return user ? (user.name || user.Username || userId) : userId;
};

const updateUserPermission = (
  index: number,
  field: 'permite_lancar_pagamentos' | 'permite_ver_relatorios' | 'permite_gerar_link_pagto',
  value: boolean
): void => {
  const user = accountUsers.value[index];
  if (!user) return;
  user[field] = toTinyint(value);
};

const loadFormasPagamento = async (): Promise<void> => {
  try {
    loadingFormas.value = true;
    const formas = await listarFormasPagamento();
    availableFormasPagamento.value = (formas as FormaPagamento[]) || [];
    log('ContasForm', 'loadFormasPagamento', 'Loaded', formas);
  } catch (error) {
    log('ContasForm', 'loadFormasPagamento', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar formas de pagamento'));
  } finally {
    loadingFormas.value = false;
  }
};

const loadUsers = async (): Promise<void> => {
  try {
    loadingUsers.value = true;

    const allFranquias = permissionsService.getFranquias() as Record<string, { Dados?: { Nome?: string } }>;

    const userMap = new Map<string, AvailableUser>();

    for (const franquiaId in allFranquias) {
      const funcionarios = permissionsService.getFuncionariosByFranquiaID(Number(franquiaId)) as Record<string, { funcionario_nome?: string }>;

      for (const funcId in funcionarios) {
        const funcionario = funcionarios[funcId];
        // Inativo não entra em select nenhum (chamado 2026-0011)
        if ((funcionario as { funcionario_ativo?: boolean }).funcionario_ativo === false) continue;

        if (!userMap.has(funcId)) {
          userMap.set(funcId, {
            sub: funcId,
            name: funcionario.funcionario_nome,
            Username: funcionario.funcionario_nome,
            franquias: []
          });
        }

        const entry = userMap.get(funcId);
        if (entry) {
          entry.franquias = entry.franquias || [];
          entry.franquias.push({
            franquia_id: franquiaId,
            franquia_nome: allFranquias[franquiaId].Dados?.Nome
          });
        }
      }
    }

    availableUsers.value = Array.from(userMap.values());

    log('ContasForm', 'loadUsers', 'Loaded users from cache (instant)', {
      count: availableUsers.value.length
    });
  } catch (error) {
    log('ContasForm', 'loadUsers', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar usuários'));
  } finally {
    loadingUsers.value = false;
  }
};

const addUser = (): void => {
  if (!selectedUserToAdd.value) return;

  accountUsers.value.push({
    usuario_id: selectedUserToAdd.value,
    status: 1,
    permite_lancar_pagamentos: 0,
    permite_ver_relatorios: 0,
    permite_gerar_link_pagto: 0
  });

  selectedUserToAdd.value = '';
  log('ContasForm', 'addUser', 'User added');
};

const removeUser = (index: number): void => {
  accountUsers.value.splice(index, 1);
  log('ContasForm', 'removeUser', 'User removed');
};

const loadForEdit = async (): Promise<void> => {
  if (!props.contaId) return;

  try {
    isLoading.value = true;
    log('ContasForm', 'loadForEdit', 'Loading account', props.contaId);

    const conta = (await fetchContaById(props.contaId)) as ContaRecord;

    formData.value = {
      conta_nome: conta.conta_nome || '',
      conta_tipo: conta.conta_tipo || '',
      conta_franquia_id: conta.conta_franquia_id || null,
      conta_provedor: conta.conta_provedor || '',
      conta_id_externo: conta.conta_id_externo || '',
      conta_permite_gerar_link_pagto: conta.conta_permite_gerar_link_pagto ?? 0,
      conta_status: conta.conta_status ?? 1
    };

    selectedFormasPagamento.value = (conta.formas_pagamento || []).map(f => f.forma_pagamento_id);

    accountUsers.value = (conta.usuarios || []).map(u => ({
      usuario_id: u.usuario_id ?? '',
      status: u.status ?? 1,
      permite_lancar_pagamentos: u.permite_lancar_pagamentos ?? 0,
      permite_ver_relatorios: u.permite_ver_relatorios ?? 0,
      permite_gerar_link_pagto: u.permite_gerar_link_pagto ?? 0
    }));

    log('ContasForm', 'loadForEdit', 'Account loaded');
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('ContasForm', 'loadForEdit', 'Error loading account', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar conta: ') + message);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  errors.value = {};
  let isValid = true;

  if (!formData.value.conta_nome || formData.value.conta_nome.trim() === '') {
    errors.value.conta_nome = t('Nome é obrigatório');
    isValid = false;
  }

  if (!formData.value.conta_tipo) {
    errors.value.conta_tipo = t('Tipo é obrigatório');
    isValid = false;
  }

  if (!formData.value.conta_franquia_id) {
    errors.value.conta_franquia_id = t('Franquia é obrigatória');
    isValid = false;
  }

  return isValid;
};

const saveAccount = async (): Promise<boolean> => {
  if (!validateForm()) {
    eventBus.emit('globalMessage.error', t('Por favor, corrija os erros no formulário'));
    return false;
  }

  try {
    isLoading.value = true;
    log('ContasForm', 'saveAccount', 'Saving account', formData.value);

    let contaId: number | null | undefined = props.contaId;

    if (props.isEditMode && contaId) {
      await updateConta(contaId, {
        ...formData.value,
        conta_franquia_id: formData.value.conta_franquia_id ?? undefined,
      });
      eventBus.emit('globalMessage.success', t('Conta atualizada com sucesso!'));
    } else {
      const response = (await createConta({
        ...formData.value,
        // validateForm() guards this is non-null before saveAccount is called
        conta_franquia_id: formData.value.conta_franquia_id!,
      })) as ContaCreateResponse;
      contaId = response?.dados?.conta_id ?? null;
      eventBus.emit('globalMessage.success', t('Conta criada com sucesso!'));
    }

    if (contaId && selectedFormasPagamento.value.length > 0) {
      await updateFormasPagamento(contaId, selectedFormasPagamento.value);
      log('ContasForm', 'saveAccount', 'Payment methods updated');
    }

    if (contaId && accountUsers.value.length > 0) {
      await updateUsuarios(contaId, accountUsers.value);
      log('ContasForm', 'saveAccount', 'Users updated');
    }

    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('ContasForm', 'saveAccount', 'Error saving account', error);
    eventBus.emit('globalMessage.error', t('Erro ao salvar conta: ') + message);
    return false;
  } finally {
    isLoading.value = false;
  }
};

const resetForm = (): void => {
  formData.value = {
    conta_nome: '',
    conta_tipo: '',
    conta_franquia_id: null,
    conta_provedor: '',
    conta_id_externo: '',
    conta_permite_gerar_link_pagto: 0,
    conta_status: 1
  };
  selectedFormasPagamento.value = [];
  accountUsers.value = [];
  errors.value = {};
};

onMounted(() => {
  loadFormasPagamento();
  loadUsers();
});

defineExpose({
  loadForEdit,
  saveAccount,
  resetForm
});
</script>

<style scoped>
.contas-form {
  max-width: 100%;
}

.form-container {
  padding: 1rem;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
}

.form-field {
  flex: 1 1 calc(50% - 10px);
  min-width: 280px;
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.loading-container {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 1rem;
  justify-content: center;
}

.selected-count {
  margin-top: 20px;
  padding-left: 10px;
}

.add-user-section {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
  margin-top: 20px;
}

.user-select-field {
  flex: 1 1 65%;
  min-width: 250px;
}

.user-button-field {
  flex: 1 1 30%;
  min-width: 150px;
}

.full-width-button {
  width: 100%;
  margin-top: 1.5rem;
}

/* Users Permission Grid */
.users-grid {
  margin-top: 20px;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 6px;
  overflow: hidden;
}

.users-grid-header,
.users-grid-row {
  display: grid;
  grid-template-columns: 2fr 1.2fr 1.2fr 1.4fr 100px;
  align-items: center;
}

.users-grid-header {
  background: var(--p-content-hover-background, #f9fafb);
  font-weight: 600;
  border-bottom: 1px solid var(--p-content-border-color, #e5e7eb);
}

.users-grid-row {
  border-bottom: 1px solid var(--p-content-border-color, #f1f5f9);
}

.users-grid-row:last-child {
  border-bottom: none;
}

.users-grid-cell {
  padding: 0.75rem 1rem;
}

.users-grid-cell.center {
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
}

@media (max-width: 992px) {
  .form-field {
    flex: 1 1 100%;
  }

  .user-select-field,
  .user-button-field {
    flex: 1 1 100%;
  }
}

@media (max-width: 768px) {
  .form-container {
    padding: 0.5rem;
  }

  .form-row {
    gap: 15px;
    margin-bottom: 15px;
  }

  .form-field {
    min-width: 100%;
  }

  .users-grid-header,
  .users-grid-row {
    grid-template-columns: 1.5fr 1fr 1fr 1fr 80px;
    font-size: 0.85rem;
  }

  .users-grid-cell {
    padding: 0.5rem;
  }
}
</style>
